import { inject, Injectable, signal } from '@angular/core';
import { collection, Firestore, getDocs } from '@angular/fire/firestore';
import { User } from '../models';
import { AuthStore } from '../auth/auth.store';

@Injectable({ providedIn: 'root' })
export class UserDirectoryService {
  private readonly firestore = inject(Firestore);
  private readonly authStore = inject(AuthStore);

  private readonly _users = signal<User[]>([]);
  private readonly _loaded = signal(false);

  readonly users = this._users.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  private get tenantId(): string {
    const id = this.authStore.tenantId();
    if (!id) throw new Error('No tenant selected');
    return id;
  }

  async loadUsers(): Promise<void> {
    if (this._loaded()) return;
    const ref = collection(this.firestore, `tenants/${this.tenantId}/users`);
    const snap = await getDocs(ref);
    this._users.set(
      snap.docs.map((docSnap) => ({
        id: docSnap.id,
        email: docSnap.data()['email'],
        displayName: docSnap.data()['displayName'],
        role: docSnap.data()['role'],
        roleId: docSnap.data()['roleId'] ?? null,
        status: docSnap.data()['status'],
        tenantId: this.tenantId,
        createdAt: docSnap.data()['createdAt']?.toDate?.() ?? new Date(),
        lastLoginAt: docSnap.data()['lastLoginAt']?.toDate?.() ?? null,
      }))
    );
    this._loaded.set(true);
  }

  getDisplayName(userId: string): string {
    return this._users().find((user) => user.id === userId)?.displayName ?? 'Unknown';
  }
}
